import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { PaginationInterface } from '../../interfaces/responses/pagination.interface';
import { ApplicationForm, ApplicationFormQueryParams, FormFieldConfig } from '../../interfaces/responses/application-form.interface';
import { CreateApplicationFormRequest, UpdateApplicationFormRequest, ApplicationFormSubmissionRequest } from '../../interfaces/requests/application-form.interface';
import { toHttpParams } from '../../utils/app.util';

const env = environment;

@Injectable({ providedIn: 'root' })
export class ApplicationFormService {
  private http = inject(HttpClient);

  private getToken(): string {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('tokenn') ?? '';
    }
    return '';
  }

  private authHeaders(): Record<string, string> {
    return { Authorization: `Bearer ${this.getToken()}` };
  }

  getForms(queryParams: ApplicationFormQueryParams): Observable<PaginationInterface<ApplicationForm[]>> {
    return this.http.get<PaginationInterface<ApplicationForm[]>>(`${env.apiUrl}/forms`, {
      params: toHttpParams(queryParams),
      headers: this.authHeaders(),
    }).pipe(catchError(err => throwError(() => err)));
  }

  getDefaultFields(): Observable<FormFieldConfig[]> {
    return this.http.get<FormFieldConfig[]>(`${env.apiUrl}/form-fields/defaults`, {
      headers: this.authHeaders(),
    }).pipe(catchError(err => throwError(() => err)));
  }

  createForm(data: CreateApplicationFormRequest): Observable<ApplicationForm> {
    return this.http.post<ApplicationForm>(`${env.apiUrl}/forms`, data, {
      headers: this.authHeaders(),
    }).pipe(catchError(err => throwError(() => err)));
  }

  createFormFields(formId: string, fields: FormFieldConfig[]): Observable<unknown> {
    return this.http.post<unknown>(`${env.apiUrl}/form-fields/custom`, { formId, fields }, {
      headers: this.authHeaders(),
    }).pipe(catchError(err => throwError(() => err)));
  }

  deleteForm(formId: string): Observable<unknown> {
    return this.http.delete<unknown>(`${env.apiUrl}/forms/${formId}`, {
      headers: this.authHeaders(),
    }).pipe(catchError(err => throwError(() => err)));
  }

  getFormById(id: string): Observable<ApplicationForm> {
    return this.http.get<ApplicationForm>(`${env.apiUrl}/forms/${id}`, {
      headers: this.authHeaders(),
    }).pipe(catchError(err => throwError(() => err)));
  }

  updateFormFields(formId: string, fields: FormFieldConfig[]): Observable<unknown> {
    return this.http.patch<unknown>(`${env.apiUrl}/form-fields/custom`, { formId, fields }, {
      headers: this.authHeaders(),
    }).pipe(catchError(err => throwError(() => err)));
  }

  updateForm(id: string, data: UpdateApplicationFormRequest): Observable<ApplicationForm> {
    return this.http.patch<ApplicationForm>(`${env.apiUrl}/forms/${id}`, data, {
      headers: this.authHeaders(),
    }).pipe(catchError(err => throwError(() => err)));
  }

  getFormByToken(token: string): Observable<ApplicationForm> {
    return this.http.get<ApplicationForm>(`${env.apiUrl}/forms/by-token/${token}`)
      .pipe(catchError(err => throwError(() => err)));
  }

  submitForm(data: ApplicationFormSubmissionRequest): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${env.apiUrl}/forms/submit`, data)
      .pipe(catchError(err => throwError(() => err)));
  }
}
